import React from "react";
import { ChevronDown, AlertTriangle, Shield, Clock, DollarSign } from "lucide-react";

const PREPAY_TYPES = [
  { value: "none", label: "None", desc: "No penalty for early payoff", icon: Shield, color: "text-emerald-600" },
  { value: "step_down", label: "Step-Down", desc: "Penalty decreases over time", icon: ChevronDown, color: "text-blue-600" },
  { value: "fixed_fee", label: "Fixed Fee", desc: "Flat penalty amount or %", icon: DollarSign, color: "text-amber-600" },
  { value: "min_interest", label: "Min Interest Guarantee", desc: "Minimum months of interest", icon: Clock, color: "text-purple-600" },
];

/**
 * Generate a plain English summary of the prepayment structure.
 */
export function formatPrepaymentSummary(structure, loanAmount, rate) {
  if (!structure || !structure.type || structure.type === "none") return "None";

  if (structure.type === "step_down") {
    const periods = structure.periods || [];
    if (periods.length === 0) return "Step-down (no periods defined)";
    const parts = periods.map(p => `${p.pct}%`).join(" / ");
    const totalMonths = periods.reduce((s, p) => s + (p.months || 3), 0);
    return `Step-down: ${parts} over ${totalMonths} months`;
  }

  if (structure.type === "fixed_fee") {
    const pct = structure.penalty_pct || 0;
    const protectedMonths = structure.protected_months || 6;
    const feeAmount = loanAmount ? Math.round(loanAmount * pct / 100) : null;
    const feeStr = feeAmount ? ` ($${feeAmount.toLocaleString()})` : "";
    return `${pct}% fixed fee${feeStr} if paid off before month ${protectedMonths}`;
  }

  if (structure.type === "min_interest") {
    const minMonths = structure.min_months || 6;
    const minInterest = (loanAmount && rate) ? Math.round(loanAmount * (rate / 100) * minMonths / 12) : null;
    const intStr = minInterest ? ` ($${minInterest.toLocaleString()} minimum)` : "";
    return `${minMonths}-month minimum interest guarantee${intStr}`;
  }

  return "Unknown structure";
}

/**
 * Convert legacy string prepayment_penalty to a structure object.
 */
export function parseLegacyPrepayment(val) {
  if (!val || val === "None" || val === "none") return { type: "none" };
  // For any other legacy string, return as-is with none type
  return { type: "none" };
}

/**
 * Convert structure to flat string for backward compat.
 */
export function structureToString(structure, loanAmount, rate) {
  return formatPrepaymentSummary(structure, loanAmount, rate);
}

export default function PrepaymentPenaltyInput({ value, onChange, loanAmount, rate, compact = false }) {
  const structure = value || { type: "none" };
  const selectedType = structure.type || "none";

  const updateType = (type) => {
    if (type === "none") {
      onChange({ type: "none" });
    } else if (type === "step_down") {
      onChange({
        type: "step_down",
        periods: [
          { months: 3, pct: 3, label: "Months 1-3" },
          { months: 3, pct: 2, label: "Months 4-6" },
          { months: 3, pct: 1, label: "Months 7-9" },
        ],
      });
    } else if (type === "fixed_fee") {
      onChange({ type: "fixed_fee", penalty_pct: 2, protected_months: 6 });
    } else if (type === "min_interest") {
      onChange({ type: "min_interest", min_months: 6 });
    }
  };

  const updatePeriod = (idx, field, val) => {
    const periods = [...(structure.periods || [])];
    periods[idx] = { ...periods[idx], [field]: Number(val) };
    // Update labels
    let month = 1;
    periods.forEach(p => {
      const end = month + (p.months || 3) - 1;
      p.label = `Months ${month}-${end}`;
      month = end + 1;
    });
    onChange({ ...structure, periods });
  };

  const inputClass = compact
    ? "w-full text-xs border border-[#EDE8DC] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-300"
    : "w-full rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-400 transition-colors";

  const selectClass = compact
    ? "w-full text-xs border border-[#EDE8DC] rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-300"
    : "w-full rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-400 transition-colors";

  const labelClass = compact
    ? "text-[10px] text-[#888] uppercase tracking-wider font-semibold mb-0.5 block"
    : "text-[10px] text-[#888] uppercase tracking-wider font-semibold mb-1 block";

  const cardBg = compact ? "bg-[#faf7f2] border-[#EDE8DC]" : "bg-[#111] border-[#2a2a2a]";
  const textMain = compact ? "text-[#333]" : "text-white";
  const textSub = compact ? "text-[#888]" : "text-[#999]";

  // Calc min interest for display
  const minInterest = (structure.type === "min_interest" && loanAmount && rate)
    ? Math.round(loanAmount * (rate / 100) * (structure.min_months || 6) / 12)
    : null;

  return (
    <div className="space-y-2" data-testid="prepayment-penalty-input">
      {/* Type Selector */}
      <span className={labelClass}>Prepayment Penalty</span>
      <div className={`grid grid-cols-2 gap-1.5 ${compact ? '' : 'sm:grid-cols-4'}`}>
        {PREPAY_TYPES.map(pt => (
          <button key={pt.value} type="button"
            onClick={() => updateType(pt.value)}
            data-testid={`prepay-type-${pt.value}`}
            className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-left transition-all ${
              selectedType === pt.value
                ? compact ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200" : "border-emerald-400 bg-emerald-400/10 ring-1 ring-emerald-400/50"
                : compact ? "border-[#EDE8DC] bg-white hover:border-[#ccc]" : "border-[#2a2a2a] bg-[#111] hover:border-[#444]"
            }`}
          >
            <pt.icon className={`h-3 w-3 flex-shrink-0 ${selectedType === pt.value ? pt.color : compact ? "text-[#aaa]" : "text-[#666]"}`} />
            <div>
              <div className={`text-[10px] font-bold ${selectedType === pt.value ? (compact ? "text-[#333]" : "text-white") : (compact ? "text-[#666]" : "text-[#888]")}`}>
                {pt.label}
              </div>
              {!compact && <div className="text-[8px] text-[#666]">{pt.desc}</div>}
            </div>
          </button>
        ))}
      </div>

      {/* Step-Down Fields */}
      {selectedType === "step_down" && (
        <div className={`rounded-xl border ${cardBg} p-3 space-y-2`} data-testid="prepay-step-down-fields">
          <div className={`text-[9px] uppercase tracking-wider font-semibold ${textSub}`}>Step-Down Schedule</div>
          {(structure.periods || []).map((period, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`step-period-${i}`}>
              <span className={`text-[10px] w-20 flex-shrink-0 ${textSub}`}>{period.label || `Period ${i + 1}`}</span>
              <input type="number" min="1" max="24" value={period.months || 3}
                onChange={e => updatePeriod(i, "months", e.target.value)}
                className={`${inputClass} w-16 text-center`}
                data-testid={`step-months-${i}`}
              />
              <span className={`text-[10px] ${textSub}`}>mo @</span>
              <input type="number" min="0" max="10" step="0.5" value={period.pct || 0}
                onChange={e => updatePeriod(i, "pct", e.target.value)}
                className={`${inputClass} w-16 text-center`}
                data-testid={`step-pct-${i}`}
              />
              <span className={`text-[10px] ${textSub}`}>%</span>
            </div>
          ))}
          <div className={`text-[10px] ${textSub} mt-1 italic`}>
            {formatPrepaymentSummary(structure)}
          </div>
        </div>
      )}

      {/* Fixed Fee Fields */}
      {selectedType === "fixed_fee" && (
        <div className={`rounded-xl border ${cardBg} p-3 space-y-2`} data-testid="prepay-fixed-fee-fields">
          <div className={`text-[9px] uppercase tracking-wider font-semibold ${textSub}`}>Fixed Fee Terms</div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className={labelClass}>Penalty (%)</label>
              <input type="number" min="0" max="10" step="0.5"
                value={structure.penalty_pct || 2}
                onChange={e => onChange({ ...structure, penalty_pct: Number(e.target.value) })}
                className={inputClass}
                data-testid="fixed-pct"
              />
            </div>
            <div className="flex-1">
              <label className={labelClass}>Protected Period (months)</label>
              <input type="number" min="1" max="36"
                value={structure.protected_months || 6}
                onChange={e => onChange({ ...structure, protected_months: Number(e.target.value) })}
                className={inputClass}
                data-testid="fixed-months"
              />
            </div>
          </div>
          {loanAmount > 0 && (
            <div className={`text-[10px] ${textSub} mt-1`}>
              Fee: <span className={`font-bold ${textMain}`}>${Math.round(loanAmount * (structure.penalty_pct || 2) / 100).toLocaleString()}</span> if paid before month {structure.protected_months || 6}
            </div>
          )}
        </div>
      )}

      {/* Min Interest Fields */}
      {selectedType === "min_interest" && (
        <div className={`rounded-xl border ${cardBg} p-3 space-y-2`} data-testid="prepay-min-interest-fields">
          <div className={`text-[9px] uppercase tracking-wider font-semibold ${textSub}`}>Minimum Interest Guarantee</div>
          <div>
            <label className={labelClass}>Minimum Months</label>
            <input type="number" min="1" max="24"
              value={structure.min_months || 6}
              onChange={e => onChange({ ...structure, min_months: Number(e.target.value) })}
              className={`${inputClass} w-24`}
              data-testid="min-months"
            />
          </div>
          {minInterest !== null && (
            <div className={`rounded-lg border ${compact ? "border-amber-200 bg-amber-50" : "border-amber-500/30 bg-amber-500/10"} p-2.5 mt-1`}>
              <div className={`text-[10px] ${compact ? "text-amber-700" : "text-amber-400"}`}>
                Borrower must pay at least <span className="font-bold">${minInterest.toLocaleString()}</span> in interest regardless of payoff date
              </div>
              <div className={`text-[9px] ${compact ? "text-amber-600" : "text-amber-500/70"} mt-0.5`}>
                (${loanAmount?.toLocaleString()} x {rate}% x {structure.min_months || 6}/12)
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
